import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://captainx.ai';

// Organization schema - used on all pages (bilingual)
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "CaptainX",
  "alternateName": "كابتن إكس",
  "url": BASE_URL,
  "logo": `${BASE_URL}/images/og-image.webp`,
  "description": "AI-Powered Sports Technology Company based in Saudi Arabia",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "SA",
    "addressLocality": "Riyadh"
  },
  "sameAs": [
    "https://www.linkedin.com/company/captainx",
    "https://twitter.com/captainx"
  ],
  "foundingDate": "2023",
  "industry": "Sports Technology"
};

// Arabic keywords for Saudi Arabia market
const arabicKeywords = "كابتن إكس, تقنية الرياضة, الذكاء الاصطناعي, السعودية, أكاديميات رياضية, اتحادات رياضية, بطولات, رياضة, تحليل رياضي, منصة رياضية, رؤية 2030, الرياض";
const englishKeywords = "CaptainX, sports technology, AI, Saudi Arabia, sports academies, federations, tournaments, sports analytics, sports platform, Vision 2030, Riyadh";

const SEO = ({
  title,
  titleAr,
  description,
  descriptionAr,
  keywords,
  keywordsAr,
  path = '/',
  language = 'en',
  type = 'website',
  image,
  article = null,
  noindex = false
}) => {
  // Base URL without query params (for canonical when viewing default/English)
  const baseUrl = `${BASE_URL}${path}`;

  // URL variants for hreflang
  const englishUrl = baseUrl;
  const arabicUrl = `${baseUrl}${path.includes('?') ? '&' : '?'}lang=ar`;

  // Current URL based on language
  const currentUrl = language === 'ar' ? arabicUrl : englishUrl;

  // Canonical URL - always point to the language-specific version
  const canonicalUrl = currentUrl;

  const ogImage = image || `${BASE_URL}/images/og-image.webp`;

  // Use appropriate title/description/keywords based on language
  const pageTitle = language === 'ar' ? (titleAr || title) : title;
  const pageDescription = language === 'ar' ? (descriptionAr || description) : description;
  const pageKeywords = language === 'ar' ? (keywordsAr || arabicKeywords) : (keywords || englishKeywords);

  // Build article schema if article data is provided
  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "image": article.image || ogImage,
    "datePublished": article.datePublished,
    "dateModified": article.dateModified || article.datePublished,
    "author": {
      "@type": "Organization",
      "name": "CaptainX"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CaptainX",
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/images/og-image.webp`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": currentUrl
    }
  } : null;

  // Breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": BASE_URL
      },
      ...(path !== '/' ? [{
        "@type": "ListItem",
        "position": 2,
        "name": pageTitle,
        "item": currentUrl
      }] : [])
    ]
  };

  return (
    <Helmet>
      {/* HTML Language Attribute */}
      <html lang={language} dir={language === 'ar' ? 'rtl' : 'ltr'} />

      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />

      {/* Canonical URL - points to current language version */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang Tags for Bilingual Support - proper URL variants */}
      <link rel="alternate" hreflang="en" href={englishUrl} />
      <link rel="alternate" hreflang="ar" href={arabicUrl} />
      <link rel="alternate" hreflang="x-default" href={englishUrl} />

      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="CaptainX" />
      <meta property="og:locale" content={language === 'ar' ? 'ar_SA' : 'en_US'} />
      <meta property="og:locale:alternate" content={language === 'ar' ? 'en_US' : 'ar_SA'} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@captainx_ai" />
      <meta name="twitter:creator" content="@captainx_ai" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={pageTitle} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* JSON-LD Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      {/* JSON-LD Structured Data - Breadcrumb */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>

      {/* JSON-LD Structured Data - Article (if applicable) */}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
